import { t } from "i18next";
import { questionMatchesAnyLanguage } from "../../../../utils/translationUtils";
import styles from "./FeedbackPanel.module.scss";
import { ChatJessMessage, FileAttachment, MediaType } from "../types";
import {
  downloadFile,
  isAgentProxyUrl,
} from "../../../../services/process/downloadFile";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Picker, { EmojiClickData, Theme } from "emoji-picker-react";
import AudioRecorder from "./AudioRecorder";
import { motion, AnimatePresence } from "framer-motion";
import { formatFileSize } from "../../../../utils";
import { getFileIcon, getFileIconColor } from "../../../../utils/fileIcons";

const MAX_CHARS = 800;

interface FeedbackPanelProps {
  activeTab: "report" | "attachments";
  closeFeedbackPanel: () => void;
  feedbackMsg: ChatJessMessage | null;
  feedbackPanelClass: string;
  handleSubmitAnswer: (
    feedbackId: string,
    answer: string,
    questionIndex?: number,
    attachments?: FileAttachment[],
    fileMap?: { attachmentId: string; file: File }[],
  ) => Promise<boolean>;
  handleSuggestionTextChange: (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => void;
  setActiveTab: React.Dispatch<React.SetStateAction<"report" | "attachments">>;
  suggestionText: string;
}

interface ObjectUrlImageProps {
  file: File | undefined;
  alt: string;
}

const ObjectUrlImage = ({ file, alt }: ObjectUrlImageProps) => {
  const url = useMemo(() => {
    return file ? URL.createObjectURL(file) : null;
  }, [file]);

  useEffect(() => {
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [url]);

  if (!url) {
    return null;
  }

  return <img src={url} alt={alt} />;
};

const FeedbackPanel = ({
  activeTab,
  closeFeedbackPanel,
  feedbackMsg,
  feedbackPanelClass,
  handleSubmitAnswer,
  handleSuggestionTextChange,
  setActiveTab,
  suggestionText,
}: FeedbackPanelProps) => {
  const [additionalAnswers, setAdditionalAnswers] = useState<{
    [key: number]: string;
  }>({});
  const suggestionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const additionalTextareaRefs = useRef<{
    [key: number]: HTMLTextAreaElement | null;
  }>({});

  // New state for input form features (main + additional questions)
  const [activeEmojiFor, setActiveEmojiFor] = useState<"main" | number | null>(
    null,
  );
  const [activeAttachmentFor, setActiveAttachmentFor] = useState<
    "main" | number | null
  >(null);
  const [audioRecorderState, setAudioRecorderState] = useState<
    "idle" | "recording" | "paused"
  >("idle");
  const [feedbackAttachments, setFeedbackAttachments] = useState<
    FileAttachment[]
  >([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragOverFor, setIsDragOverFor] = useState<"main" | number | null>(
    null,
  );
  const attachmentIdToFileRef = useRef<Map<string, File>>(new Map());

  // Additional questions: same input form (attachments, emoji, audio, send)
  const [additionalAttachments, setAdditionalAttachments] = useState<
    Record<number, FileAttachment[]>
  >({});
  const additionalAttachmentIdToFileRef = useRef<Map<string, File>>(new Map());
  const [activeInputForModals, setActiveInputForModals] = useState<
    "main" | number | null
  >(null);
  const [audioRecorderFor, setAudioRecorderFor] = useState<
    "main" | number | null
  >(null);

  // Same allowed file types as ChatJessPage for drag-and-drop
  const ALLOWED_FILE_TYPES: Record<string, string> = {
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".gif": "image/gif",
    ".webp": "image/webp",
    ".pdf": "application/pdf",
    ".txt": "text/plain",
    ".csv": "text/csv",
    ".mpeg": "audio/mpeg",
    ".mp3": "audio/mp3",
    ".ogg": "audio/ogg",
    ".wav": "audio/wav",
    ".webm": "audio/webm",
    ".mp4": "audio/mp4",
  };

  const getAllowedExtensions = (): string => {
    return Object.keys(ALLOWED_FILE_TYPES).join(", ");
  };

  // Dismiss drag overlay on Escape (when user gives up dropping files)
  useEffect(() => {
    if (!isDragOver) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsDragOver(false);
        setIsDragOverFor(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isDragOver]);

  const autoResizeSuggestionTextarea = useCallback(() => {
    if (suggestionTextareaRef.current) {
      const textarea = suggestionTextareaRef.current;
      textarea.style.height = "auto";

      if (suggestionText.length <= MAX_CHARS) {
        const scrollHeight = textarea.scrollHeight;
        textarea.style.height = `${Math.min(scrollHeight, 200)}px`;
        textarea.style.overflowY = scrollHeight > 200 ? "auto" : "hidden";
      } else {
        textarea.style.height = "200px";
        textarea.style.overflowY = "auto";
      }
    }
  }, [suggestionText.length]);

  useEffect(() => {
    autoResizeSuggestionTextarea();
  }, [autoResizeSuggestionTextarea]);

  const isDarkTheme = localStorage.getItem("theme") === "dark";

  const autoResizeAdditionalTextarea = (index: number) => {
    const textarea = additionalTextareaRefs.current[index];
    if (textarea) {
      textarea.style.height = "auto";
      const text = additionalAnswers[index] || "";

      if (text.length <= MAX_CHARS) {
        const scrollHeight = textarea.scrollHeight;
        textarea.style.height = `${Math.min(scrollHeight, 200)}px`;
        textarea.style.overflowY = scrollHeight > 200 ? "auto" : "hidden";
      } else {
        textarea.style.height = "200px";
        textarea.style.overflowY = "auto";
      }
    }
  };

  const handleAdditionalAnswerChange = (index: number, value: string) => {
    setAdditionalAnswers((prev) => ({
      ...prev,
      [index]: value,
    }));
    setTimeout(() => autoResizeAdditionalTextarea(index), 0);
  };

  // Input form handlers (main and additional questions via forIndex)
  const handleEmojiButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    forIndex: "main" | number = "main",
  ) => {
    event.stopPropagation();
    setActiveEmojiFor((prev) => (prev === forIndex ? null : forIndex));
  };

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const forIndex = activeEmojiFor;
    if (forIndex === "main") {
      handleSuggestionTextChange({
        target: { value: suggestionText + emojiData.emoji },
      } as React.ChangeEvent<HTMLTextAreaElement>);
      if (suggestionTextareaRef.current) suggestionTextareaRef.current.focus();
    } else if (typeof forIndex === "number") {
      handleAdditionalAnswerChange(
        forIndex,
        (additionalAnswers[forIndex] || "") + emojiData.emoji,
      );
      setTimeout(() => additionalTextareaRefs.current[forIndex]?.focus(), 0);
    }
    setActiveEmojiFor(null);
  };

  const handleAttachmentButtonClick = (
    _event: React.MouseEvent<HTMLButtonElement>,
    forIndex?: "main" | number,
  ) => {
    const target = forIndex ?? "main";
    setActiveAttachmentFor((prev) => (prev === target ? null : target));
  };

  const handleAttachmentSelect = (type: "document" | "image" | "audio") => {
    const forIndex = activeAttachmentFor;
    if (forIndex === null) return;
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;

    if (type === "document") {
      input.accept = ".pdf,.txt,.csv";
    } else if (type === "image") {
      input.accept = ".jpg,.jpeg,.png,.gif,.webp";
    } else if (type === "audio") {
      input.accept = ".mp3,.mp4,.webm,.ogg,.wav";
    }

    input.onchange = async (e) => {
      const selectedFiles = (e.target as HTMLInputElement).files;
      if (selectedFiles && selectedFiles.length > 0) {
        const newAttachments: FileAttachment[] = [];
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const id =
            Date.now().toString() + Math.random().toString(36).substring(2, 11);
          if (forIndex === "main") {
            attachmentIdToFileRef.current.set(id, file);
          } else {
            additionalAttachmentIdToFileRef.current.set(
              `${forIndex}_${id}`,
              file,
            );
          }
          newAttachments.push({
            id,
            name: file.name,
            size: file.size,
            type: file.type,
            isUploading: false,
          });
        }
        if (forIndex === "main") {
          setFeedbackAttachments((prev) => [...prev, ...newAttachments]);
        } else {
          setAdditionalAttachments((prev) => ({
            ...prev,
            [forIndex]: [...(prev[forIndex] || []), ...newAttachments],
          }));
        }
      }
    };

    input.click();
    setActiveAttachmentFor(null);
  };

  const handleMicrophoneClick = (forIndex: "main" | number = "main") => {
    setAudioRecorderFor(forIndex);
    setAudioRecorderState("recording");
  };

  const handleCancelRecording = useCallback(() => {
    setAudioRecorderState("idle");
    setAudioRecorderFor(null);
    setTimeout(() => {
      if (suggestionTextareaRef.current) suggestionTextareaRef.current.focus();
    }, 100);
  }, []);

  const handleAudioRecorded = async (
    audioBlob: Blob,
    _waveformData?: number[],
    _duration?: number,
  ) => {
    const forIndex = audioRecorderFor;
    const fileName = `audio_${Date.now()}.${audioBlob.type === "audio/mp4" ? "mp4" : "webm"}`;
    const id =
      Date.now().toString() + Math.random().toString(36).substring(2, 11);
    const audioAttachment: FileAttachment = {
      id,
      name: fileName,
      size: audioBlob.size,
      type: audioBlob.type || "audio/webm",
      isUploading: false,
    };
    const file = new File([audioBlob], fileName, {
      type: audioBlob.type || "audio/webm",
    });
    if (forIndex === "main") {
      attachmentIdToFileRef.current.set(id, file);
      setFeedbackAttachments((prev) => [...prev, audioAttachment]);
      setTimeout(() => suggestionTextareaRef.current?.focus(), 100);
    } else if (typeof forIndex === "number") {
      additionalAttachmentIdToFileRef.current.set(`${forIndex}_${id}`, file);
      setAdditionalAttachments((prev) => ({
        ...prev,
        [forIndex]: [...(prev[forIndex] || []), audioAttachment],
      }));
      setTimeout(() => additionalTextareaRefs.current[forIndex]?.focus(), 100);
    }
    setAudioRecorderState("idle");
    setAudioRecorderFor(null);
  };

  const renderSendButton = () => {
    return (
      <AnimatePresence mode="wait">
        {suggestionText.trim() || feedbackAttachments.length > 0 ? (
          <motion.button
            key="send"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.2 }}
            className={styles.sendButton}
            onClick={async () => {
              const fileMap = feedbackAttachments
                .filter((a) => attachmentIdToFileRef.current.has(a.id))
                .map((a) => ({
                  attachmentId: a.id,
                  file: attachmentIdToFileRef.current.get(a.id)!,
                }));
              const success = await handleSubmitAnswer(
                feedbackMsg?.feedbackId || "",
                suggestionText,
                undefined,
                feedbackAttachments.length > 0
                  ? feedbackAttachments
                  : undefined,
                fileMap.length > 0 ? fileMap : undefined,
              );
              if (success) {
                fileMap.forEach(({ attachmentId }) =>
                  attachmentIdToFileRef.current.delete(attachmentId),
                );
                setFeedbackAttachments([]);
              }
            }}
            title={t("chatjess.feedback.submitAnswer")}
          >
            <i className="fas fa-paper-plane" />
          </motion.button>
        ) : (
          <motion.button
            key="mic"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.2 }}
            className={styles.micButton}
            onClick={() => handleMicrophoneClick("main")}
            title={t("chatjess.ui.recordAudio")}
          >
            <i className="fas fa-microphone" />
          </motion.button>
        )}
      </AnimatePresence>
    );
  };

  const renderAdditionalSendButton = (index: number) => {
    const text = additionalAnswers[index] || "";
    const attachments = additionalAttachments[index] || [];
    const hasContent = text.trim() || attachments.length > 0;

    return (
      <AnimatePresence mode="wait">
        {hasContent ? (
          <motion.button
            key="send"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.2 }}
            className={styles.sendButton}
            onClick={async () => {
              const fileMap = attachments
                .filter((a) =>
                  additionalAttachmentIdToFileRef.current.has(
                    `${index}_${a.id}`,
                  ),
                )
                .map((a) => ({
                  attachmentId: a.id,
                  file: additionalAttachmentIdToFileRef.current.get(
                    `${index}_${a.id}`,
                  )!,
                }));
              const success = await handleSubmitAnswer(
                feedbackMsg?.feedbackId || "",
                text,
                index,
                attachments.length > 0 ? attachments : undefined,
                fileMap.length > 0 ? fileMap : undefined,
              );
              if (success) {
                fileMap.forEach(({ attachmentId }) =>
                  additionalAttachmentIdToFileRef.current.delete(
                    `${index}_${attachmentId}`,
                  ),
                );
                setAdditionalAttachments((prev) => {
                  const next = { ...prev };
                  delete next[index];
                  return next;
                });
                setAdditionalAnswers((prev) => {
                  const next = { ...prev };
                  delete next[index];
                  return next;
                });
              }
            }}
            title={t("chatjess.feedback.submitAnswer")}
          >
            <i className="fas fa-paper-plane" />
          </motion.button>
        ) : (
          <motion.button
            key="mic"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.2 }}
            className={styles.micButton}
            onClick={() => handleMicrophoneClick(index)}
            title={t("chatjess.ui.recordAudio")}
          >
            <i className="fas fa-microphone" />
          </motion.button>
        )}
      </AnimatePresence>
    );
  };

  const removeAttachment = (attachmentId: string) => {
    attachmentIdToFileRef.current.delete(attachmentId);
    setFeedbackAttachments((prev) =>
      prev.filter((att) => att.id !== attachmentId),
    );
  };

  const removeAdditionalAttachment = (index: number, attachmentId: string) => {
    additionalAttachmentIdToFileRef.current.delete(`${index}_${attachmentId}`);
    setAdditionalAttachments((prev) => ({
      ...prev,
      [index]: (prev[index] || []).filter((att) => att.id !== attachmentId),
    }));
  };

  const handleDragOver = (
    e: React.DragEvent,
    forIndex: "main" | number = "main",
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
      setIsDragOverFor(forIndex);
    }
  };

  const handleDragEnter = (
    e: React.DragEvent,
    forIndex: "main" | number = "main",
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
      setIsDragOverFor(forIndex);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
      setIsDragOverFor(null);
    }
  };

  const handleDrop = (e: React.DragEvent, forIndex?: "main" | number) => {
    e.preventDefault();
    e.stopPropagation();
    const target = forIndex ?? isDragOverFor ?? "main";
    setIsDragOver(false);
    setIsDragOverFor(null);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length === 0) return;

    const newAttachments: FileAttachment[] = [];
    for (let i = 0; i < droppedFiles.length; i++) {
      const file = droppedFiles[i];
      const fileName = file.name.toLowerCase();
      const extension = fileName.substring(fileName.lastIndexOf("."));

      if (!Object.hasOwn(ALLOWED_FILE_TYPES, extension)) {
        continue;
      }

      const id =
        Date.now().toString() + Math.random().toString(36).substring(2, 11);
      if (target === "main") {
        attachmentIdToFileRef.current.set(id, file);
      } else {
        additionalAttachmentIdToFileRef.current.set(`${target}_${id}`, file);
      }
      newAttachments.push({
        id,
        name: file.name,
        size: file.size,
        type: file.type,
        isUploading: false,
      });
    }

    if (newAttachments.length > 0) {
      if (target === "main") {
        setFeedbackAttachments((prev) => [...prev, ...newAttachments]);
      } else {
        setAdditionalAttachments((prev) => ({
          ...prev,
          [target]: [...(prev[target] || []), ...newAttachments],
        }));
      }
    }
  };

  const shouldShowAttachmentAndEmojiButtons = () => {
    return audioRecorderState === "idle";
  };

  // Close emoji picker and attachment modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        !target.closest('[data-testid="emoji-picker"]') &&
        !target.closest(".emoji-button") &&
        !target.closest(".emoji-picker-container")
      ) {
        setActiveEmojiFor(null);
      }

      if (
        !target.closest(".attachment-modal") &&
        !target.closest(".attachment-button")
      ) {
        setActiveAttachmentFor(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closePanel = () => {
    setAdditionalAnswers({});
    closeFeedbackPanel();
  };

  return (
    <>
      <aside
        className={`${feedbackPanelClass} ${styles[isDarkTheme ? "darkTheme" : "lightTheme"]}`}
      >
        <div
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          {/* Header do Painel */}
          <div className={styles.feedbackPanelHeader}>
            <h2 className={styles.feedbackPanelTitle}>
              {t("chatjess.feedback.panelTitle")}
            </h2>
            <button
              className={styles.feedbackPanelClose}
              onClick={closePanel}
              aria-label={t("common.close") || "Fechar"}
              title={t("common.close") || "Fechar"}
            >
              <i className="fas fa-times" />
            </button>
          </div>

          {/* Sistema de Abas */}
          <div className={styles.feedbackPanelTabs}>
            <button
              className={`${styles.feedbackPanelTab} ${activeTab === "report" ? styles.active : ""}`}
              onClick={() => setActiveTab("report")}
            >
              {t("chatjess.feedback.reportTab")}
            </button>
            <button
              className={`${styles.feedbackPanelTab} ${activeTab === "attachments" ? styles.active : ""}`}
              onClick={() => setActiveTab("attachments")}
            >
              {t("chatjess.feedback.attachmentsTab")}
            </button>
          </div>

          {/* Conteúdo das Abas */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {activeTab === "report" ? (
              <div
                className={`${styles.feedbackPanelTabContent} ${styles.reportTab} ${activeTab === "report" ? styles.active : ""}`}
              >
                {/* Show completion status */}
                {feedbackMsg?.supportMessages?.every((msg) => msg.answer) && (
                  <div className={styles.allQuestionsAnsweredMessage}>
                    ✅ {t("chatjess.feedback.allQuestionsAnswered")}
                  </div>
                )}

                <div className={styles.feedbackSection}>
                  <h2 className={styles.feedbackSectionTitle}>
                    {feedbackMsg?.supportType === "requiresHumanAction"
                      ? t("chatjess.feedback.jessRequest")
                      : t("chatjess.feedback.jessQuestion")}
                  </h2>

                  <div className={styles.feedbackContext}>
                    <strong>
                      {feedbackMsg?.supportType === "requiresHumanAction"
                        ? t("chatjess.feedback.jessRequest")
                        : t("chatjess.feedback.jessQuestion")}
                    </strong>{" "}
                    {feedbackMsg?.supportMessages?.[0]?.question ||
                      feedbackMsg?.content ||
                      t("chatjess.contentNotAvailable")}
                  </div>

                  {feedbackMsg?.feedbackDetails && (
                    <>
                      <h3 className={styles.feedbackSectionSubtitle}>
                        {t("chatjess.feedback.situationDetails")}
                      </h3>
                      <div className={styles.feedbackContext}>
                        <strong>{t("chatjess.feedback.context")}</strong>{" "}
                        {feedbackMsg?.feedbackDetails}
                      </div>
                    </>
                  )}

                  {/* Link to attachments if available */}
                  {feedbackMsg?.supportMessages?.some(
                    (msg) =>
                      msg.media ||
                      (msg.answerMedia && msg.answerMedia.length > 0),
                  ) && (
                    <div className={styles.viewAttachmentsLink}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab("attachments");
                        }}
                      >
                        <i className="fas fa-paperclip"></i>{" "}
                        {t("chatjess.feedback.viewAttachments", {
                          count:
                            feedbackMsg?.supportMessages?.reduce(
                              (sum, msg) =>
                                sum +
                                (msg.media ? 1 : 0) +
                                (msg.answerMedia?.length || 0),
                              0,
                            ) ?? 0,
                        })}
                      </a>
                    </div>
                  )}
                </div>

                {/* Display all questions and answers from supportMessages */}
                {feedbackMsg?.supportMessages?.map((supportMsg, index) => (
                  <div key={index} className={styles.feedbackSection}>
                    {index === 0 ? (
                      // First question - main display
                      <>
                        <h2 className={styles.feedbackSectionTitle}>
                          {supportMsg.answer ||
                          (supportMsg.answerMedia &&
                            supportMsg.answerMedia.length > 0) ||
                          feedbackMsg?.isCompleted
                            ? t("chatjess.feedback.answered")
                            : t("chatjess.feedback.sendAnswer")}
                        </h2>

                        {supportMsg.answer ||
                        (supportMsg.answerMedia &&
                          supportMsg.answerMedia.length > 0) ||
                        feedbackMsg?.isCompleted ? (
                          // Show answer if already responded OR if isCompleted
                          <div className={styles.feedbackContext}>
                            {supportMsg.answer || ""}
                            {supportMsg.answerMedia &&
                              supportMsg.answerMedia.length > 0 && (
                                <div
                                  className={styles.answerAttachmentsList}
                                  style={{ marginTop: "8px" }}
                                >
                                  <i
                                    className="fas fa-paperclip"
                                    style={{
                                      marginRight: "6px",
                                      fontSize: "12px",
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {supportMsg.answerMedia.map(
                                      (media: MediaType, i: number) => (
                                        <span key={i}>
                                          {media.filename ||
                                            t(
                                              "chatjess.attachments.attachment",
                                            )}
                                          {i <
                                          supportMsg.answerMedia!.length - 1
                                            ? ", "
                                            : ""}
                                        </span>
                                      ),
                                    )}
                                  </span>
                                </div>
                              )}
                          </div>
                        ) : (
                          // Show input form if not responded AND not completed
                          <>
                            <p
                              style={{
                                color: !isDarkTheme ? "#333" : "#b0b0b0",
                                marginBottom: "16px",
                              }}
                            >
                              {t("replyToJess")}
                            </p>

                            {/* Drop zone: pending attachments + input form (same pattern as ChatJessPage) */}
                            <div
                              onDragOver={handleDragOver}
                              onDragEnter={handleDragEnter}
                              onDragLeave={handleDragLeave}
                              onDrop={handleDrop}
                              style={{ position: "relative" }}
                            >
                              {/* Preview de Anexos Pendentes - Modal Externa */}
                              {feedbackAttachments.length > 0 && (
                                <div className={styles.pendingAttachmentsModal}>
                                  <div
                                    className={styles.pendingAttachmentsHeader}
                                  >
                                    <span
                                      className={styles.pendingAttachmentsTitle}
                                    >
                                      <i
                                        className="fas fa-paperclip"
                                        style={{
                                          marginRight: "8px",
                                          fontSize: "12px",
                                        }}
                                      />
                                      {t(
                                        `chatjess.attachments.selected${feedbackAttachments.length > 1 ? "_plural" : ""}`,
                                        { count: feedbackAttachments.length },
                                      )}
                                    </span>
                                    <button
                                      className={styles.clearAllAttachmentsBtn}
                                      onClick={() => {
                                        setFeedbackAttachments([]);
                                      }}
                                      title={t(
                                        "chatjess.attachments.cancelAll",
                                      )}
                                    >
                                      <i className="fas fa-times" />
                                      {t("chatjess.attachments.cancelAll")}
                                    </button>
                                  </div>

                                  <div
                                    className={
                                      styles.pendingAttachmentsContainer
                                    }
                                  >
                                    {feedbackAttachments.map((attachment) => (
                                      <div
                                        key={attachment.id}
                                        className={styles.attachmentPreview}
                                      >
                                        <div
                                          className={styles.attachmentThumbnail}
                                        >
                                          {attachment.isUploading ? (
                                            <div
                                              style={{
                                                position: "relative",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <i
                                                className={getFileIcon(
                                                  attachment.type,
                                                  attachment.name,
                                                )}
                                                style={{
                                                  color: getFileIconColor(
                                                    attachment.type,
                                                    attachment.name,
                                                  ),
                                                  opacity: 0.5,
                                                }}
                                              />
                                              <div
                                                style={{
                                                  position: "absolute",
                                                  fontSize: "12px",
                                                  color: "#666",
                                                  top: "50%",
                                                  left: "50%",
                                                  transform:
                                                    "translate(-50%, -50%)",
                                                }}
                                              >
                                                <i className="fas fa-spinner fa-spin" />
                                              </div>
                                            </div>
                                          ) : attachment.thumbnail ? (
                                            <img
                                              src={attachment.thumbnail}
                                              alt={attachment.name}
                                            />
                                          ) : (
                                            <i
                                              className={getFileIcon(
                                                attachment.type,
                                                attachment.name,
                                              )}
                                              style={{
                                                color: getFileIconColor(
                                                  attachment.type,
                                                  attachment.name,
                                                ),
                                              }}
                                            />
                                          )}
                                        </div>
                                        <div className={styles.attachmentInfo}>
                                          <div
                                            className={styles.attachmentName}
                                          >
                                            {attachment.name}
                                          </div>
                                          <div
                                            className={styles.attachmentSize}
                                          >
                                            {formatFileSize(attachment.size)}
                                          </div>
                                        </div>
                                        <button
                                          className={styles.attachmentRemove}
                                          onClick={() =>
                                            removeAttachment(attachment.id)
                                          }
                                          title={t(
                                            "chatjess.attachments.removeAttachment",
                                          )}
                                          disabled={attachment.isUploading}
                                          style={{
                                            opacity: attachment.isUploading
                                              ? 0.5
                                              : 1,
                                          }}
                                        >
                                          <i className="fas fa-times" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Full input form from ChatJessPage */}
                              <div className={styles.inputForm}>
                                {shouldShowAttachmentAndEmojiButtons() && (
                                  <button
                                    type="button"
                                    className={`${styles.inputButton} ${styles.attachmentButton} attachment-button`}
                                    title={t("chatjess.ui.attachFiles")}
                                    onClick={(e) =>
                                      handleAttachmentButtonClick(e, "main")
                                    }
                                  >
                                    <i className="fas fa-plus" />
                                  </button>
                                )}

                                {shouldShowAttachmentAndEmojiButtons() && (
                                  <button
                                    type="button"
                                    className={`${styles.inputButton} emoji-button`}
                                    onClick={(e) =>
                                      handleEmojiButtonClick(e, "main")
                                    }
                                    title={t("chatjess.ui.addEmoji")}
                                    style={{ position: "relative" }}
                                  >
                                    <i className="fas fa-smile" />
                                  </button>
                                )}

                                {audioRecorderFor === "main" &&
                                audioRecorderState !== "idle" ? (
                                  <div
                                    className={styles.audioRecorderContainer}
                                  >
                                    <AudioRecorder
                                      onAudioRecorded={handleAudioRecorded}
                                      onCancel={handleCancelRecording}
                                      isDarkTheme={isDarkTheme}
                                    />
                                  </div>
                                ) : (
                                  <>
                                    <textarea
                                      rows={1}
                                      ref={suggestionTextareaRef}
                                      value={suggestionText}
                                      onChange={handleSuggestionTextChange}
                                      onFocus={() => setIsInputFocused(true)}
                                      onBlur={() => setIsInputFocused(false)}
                                      className={styles.messageInput}
                                      placeholder={t(
                                        "chatjess.feedback.enterAnswer",
                                      )}
                                      style={{
                                        minHeight: "44px",
                                        maxHeight:
                                          suggestionText.length > MAX_CHARS
                                            ? "200px"
                                            : "auto",
                                        transition: "height 0.2s ease",
                                      }}
                                    />

                                    <div className={styles.sendButtonContainer}>
                                      {renderSendButton()}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className={styles.characterCount}>
                              {suggestionText.length}/{MAX_CHARS}
                            </div>

                            {/* Drag & Drop Overlay - must preventDefault in dragover so overlay is a valid drop target (otherwise browser opens file) */}
                            {isDragOver && (
                              <div
                                className={styles.dragDropOverlay}
                                onClick={() => setIsDragOver(false)}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onDrop={handleDrop}
                                role="presentation"
                              >
                                <div
                                  className={styles.dragDropContent}
                                  onClick={(e) => e.stopPropagation()}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <i
                                    className="fas fa-cloud-upload-alt"
                                    style={{ fontSize: "48px" }}
                                  />
                                  <div>
                                    {t("chatjess.attachments.dropFilesHere")}
                                  </div>
                                  <div>
                                    {t(
                                      "chatjess.attachments.supportedFileTypes",
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Attachment modal (main question only) */}
                            {activeAttachmentFor === "main" && (
                              <div
                                className={`${styles.attachmentModal} attachment-modal`}
                              >
                                <div
                                  className={styles.attachmentOption}
                                  onClick={() =>
                                    handleAttachmentSelect("document")
                                  }
                                >
                                  <div className={styles.attachmentIcon}>
                                    <i className="fas fa-file-alt" />
                                  </div>
                                  <span>
                                    {t("chatjess.attachments.document")}
                                  </span>
                                </div>
                                <div
                                  className={styles.attachmentOption}
                                  onClick={() =>
                                    handleAttachmentSelect("image")
                                  }
                                >
                                  <div className={styles.attachmentIcon}>
                                    <i className="fas fa-image" />
                                  </div>
                                  <span>{t("chatjess.attachments.image")}</span>
                                </div>
                                <div
                                  className={styles.attachmentOption}
                                  onClick={() =>
                                    handleAttachmentSelect("audio")
                                  }
                                >
                                  <div className={styles.attachmentIcon}>
                                    <i className="fas fa-microphone" />
                                  </div>
                                  <span>{t("chatjess.attachments.audio")}</span>
                                </div>
                              </div>
                            )}

                            {/* Emoji picker (main question only) */}
                            {activeEmojiFor === "main" && (
                              <div
                                className="emoji-picker-container"
                                style={{
                                  position: "absolute",
                                  bottom: "60px",
                                  left: "60px",
                                  zIndex: 20,
                                  borderRadius: "8px",
                                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                                }}
                              >
                                <Picker
                                  onEmojiClick={handleEmojiSelect}
                                  theme={isDarkTheme ? Theme.DARK : Theme.LIGHT}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      // Additional questions
                      <>
                        <h3 className={styles.feedbackSectionSubtitle}>
                          {(() => {
                            const shouldUseJessLabel =
                              questionMatchesAnyLanguage(
                                feedbackMsg?.supportMessages?.[index]?.question,
                              );

                            return shouldUseJessLabel
                              ? "Jesss:"
                              : t("chatjess.feedback.additionalQuestion", {
                                  number: index + 1,
                                });
                          })()}
                        </h3>
                        <div className={styles.feedbackContext}>
                          {(() => {
                            // Check if this is the last message and if isCompleted is true
                            const shouldUseJessLabel =
                              questionMatchesAnyLanguage(
                                feedbackMsg?.supportMessages?.[index]?.question,
                              );

                            if (shouldUseJessLabel) {
                              // For Jess messages, don't show "Pergunta:" prefix - just show the content
                              return supportMsg.question;
                            } else {
                              // For regular questions, show "Pergunta:" prefix
                              return (
                                <>
                                  <strong>
                                    {t("chatjess.feedback.jessQuestion")}
                                  </strong>{" "}
                                  {supportMsg.question}
                                </>
                              );
                            }
                          })()}
                        </div>

                        {(() => {
                          // Check if this is the last message and if isCompleted is true
                          const shouldUseJessLabel = questionMatchesAnyLanguage(
                            feedbackMsg?.supportMessages?.[index]?.question,
                          );

                          if (
                            supportMsg.answer ||
                            (supportMsg.answerMedia &&
                              supportMsg.answerMedia.length > 0)
                          ) {
                            // Show answer if already responded (text or media)
                            return (
                              <div
                                className={styles.feedbackContext}
                                style={{ marginTop: "8px" }}
                              >
                                <strong>{t("chatjess.feedback.answer")}</strong>{" "}
                                {supportMsg.answer}
                                {supportMsg.answerMedia &&
                                  supportMsg.answerMedia.length > 0 && (
                                    <div
                                      className={styles.answerAttachmentsList}
                                      style={{ marginTop: "8px" }}
                                    >
                                      <i
                                        className="fas fa-paperclip"
                                        style={{
                                          marginRight: "6px",
                                          fontSize: "12px",
                                        }}
                                      />
                                      <span
                                        style={{
                                          fontSize: "13px",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {supportMsg.answerMedia.map(
                                          (media: MediaType, i: number) => (
                                            <span key={i}>
                                              {media.filename ||
                                                t(
                                                  "chatjess.attachments.attachment",
                                                )}
                                              {i <
                                              supportMsg.answerMedia!.length - 1
                                                ? ", "
                                                : ""}
                                            </span>
                                          ),
                                        )}
                                      </span>
                                    </div>
                                  )}
                              </div>
                            );
                          } else if (
                            feedbackMsg?.isCompleted &&
                            !shouldUseJessLabel
                          ) {
                            // Show empty answer if isCompleted but not a Jess message
                            return (
                              <div
                                className={styles.feedbackContext}
                                style={{ marginTop: "8px" }}
                              >
                                <strong>{t("chatjess.feedback.answer")}</strong>{" "}
                                {""}
                              </div>
                            );
                          } else if (shouldUseJessLabel) {
                            // Don't show "Resposta:" section for Jess messages - the question content is Jess's response
                            return null;
                          }

                          // Full input form for unanswered additional questions (same as main)
                          const list = additionalAttachments[index] || [];
                          return (
                            <div
                              style={{
                                position: "relative",
                                marginTop: "12px",
                              }}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDragEnter={(e) => handleDragEnter(e, index)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, index)}
                            >
                              {list.length > 0 && (
                                <div className={styles.pendingAttachmentsModal}>
                                  <div
                                    className={
                                      styles.pendingAttachmentsContainer
                                    }
                                  >
                                    {list.map((attachment) => (
                                      <div
                                        key={attachment.id}
                                        className={styles.attachmentPreview}
                                      >
                                        <div
                                          className={styles.attachmentThumbnail}
                                        >
                                          {attachment.type?.startsWith(
                                            "image/",
                                          ) ||
                                          /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                            attachment.name,
                                          ) ? (
                                            <ObjectUrlImage
                                              file={additionalAttachmentIdToFileRef.current.get(
                                                `${index}_${attachment.id}`,
                                              )}
                                              alt={attachment.name}
                                            />
                                          ) : (
                                            <i
                                              className={getFileIcon(
                                                attachment.type,
                                                attachment.name,
                                              )}
                                              style={{
                                                color: getFileIconColor(
                                                  attachment.type,
                                                  attachment.name,
                                                ),
                                              }}
                                            />
                                          )}
                                        </div>
                                        <div className={styles.attachmentInfo}>
                                          <div
                                            className={styles.attachmentName}
                                          >
                                            {attachment.name}
                                          </div>
                                          <div
                                            className={styles.attachmentSize}
                                          >
                                            {formatFileSize(attachment.size)}
                                          </div>
                                        </div>
                                        <button
                                          className={styles.attachmentRemove}
                                          onClick={() =>
                                            removeAdditionalAttachment(
                                              index,
                                              attachment.id,
                                            )
                                          }
                                          title={t(
                                            "chatjess.attachments.removeAttachment",
                                          )}
                                          disabled={attachment.isUploading}
                                          style={{
                                            opacity: attachment.isUploading
                                              ? 0.5
                                              : 1,
                                          }}
                                        >
                                          <i className="fas fa-times" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className={styles.inputForm}>
                                {shouldShowAttachmentAndEmojiButtons() && (
                                  <button
                                    type="button"
                                    className={`${styles.inputButton} ${styles.attachmentButton} attachment-button`}
                                    title={t("chatjess.ui.attachFiles")}
                                    onClick={(e) =>
                                      handleAttachmentButtonClick(e, index)
                                    }
                                  >
                                    <i className="fas fa-plus" />
                                  </button>
                                )}

                                {shouldShowAttachmentAndEmojiButtons() && (
                                  <button
                                    type="button"
                                    className={`${styles.inputButton} emoji-button`}
                                    onClick={(e) =>
                                      handleEmojiButtonClick(e, index)
                                    }
                                    title={t("chatjess.ui.addEmoji")}
                                    style={{ position: "relative" }}
                                  >
                                    <i className="fas fa-smile" />
                                  </button>
                                )}

                                {audioRecorderFor === index &&
                                audioRecorderState !== "idle" ? (
                                  <div
                                    className={styles.audioRecorderContainer}
                                  >
                                    <AudioRecorder
                                      onAudioRecorded={handleAudioRecorded}
                                      onCancel={handleCancelRecording}
                                      isDarkTheme={isDarkTheme}
                                    />
                                  </div>
                                ) : (
                                  <>
                                    <textarea
                                      ref={(el) =>
                                        (additionalTextareaRefs.current[index] =
                                          el)
                                      }
                                      rows={1}
                                      value={additionalAnswers[index] || ""}
                                      onChange={(e) =>
                                        handleAdditionalAnswerChange(
                                          index,
                                          e.target.value,
                                        )
                                      }
                                      className={styles.messageInput}
                                      placeholder={t(
                                        "chatjess.feedback.enterAnswer",
                                      )}
                                      style={{
                                        minHeight: "44px",
                                        maxHeight:
                                          (additionalAnswers[index] || "")
                                            .length > MAX_CHARS
                                            ? "200px"
                                            : "auto",
                                        transition: "height 0.2s ease",
                                      }}
                                    />
                                    <div className={styles.characterCount}>
                                      {(additionalAnswers[index] || "").length}/
                                      {MAX_CHARS}
                                    </div>
                                    <div className={styles.sendButtonContainer}>
                                      {renderAdditionalSendButton(index)}
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* Drag & Drop overlay for this additional question */}
                              {isDragOverFor === index && (
                                <div
                                  className={styles.dragDropOverlay}
                                  onClick={() => {
                                    setIsDragOver(false);
                                    setIsDragOverFor(null);
                                  }}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onDrop={(e) => handleDrop(e, index)}
                                  role="presentation"
                                >
                                  <div
                                    className={styles.dragDropContent}
                                    onClick={(e) => e.stopPropagation()}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                  >
                                    <i
                                      className="fas fa-cloud-upload-alt"
                                      style={{ fontSize: "48px" }}
                                    />
                                    <div>
                                      {t("chatjess.attachments.dropFilesHere")}
                                    </div>
                                    <div>
                                      {t(
                                        "chatjess.attachments.supportedFileTypes",
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Attachment modal for this additional question */}
                              {activeAttachmentFor === index && (
                                <div
                                  className={`${styles.attachmentModal} attachment-modal`}
                                >
                                  <div
                                    className={styles.attachmentOption}
                                    onClick={() =>
                                      handleAttachmentSelect("document")
                                    }
                                  >
                                    <div className={styles.attachmentIcon}>
                                      <i className="fas fa-file-alt" />
                                    </div>
                                    <span>
                                      {t("chatjess.attachments.document")}
                                    </span>
                                  </div>
                                  <div
                                    className={styles.attachmentOption}
                                    onClick={() =>
                                      handleAttachmentSelect("image")
                                    }
                                  >
                                    <div className={styles.attachmentIcon}>
                                      <i className="fas fa-image" />
                                    </div>
                                    <span>
                                      {t("chatjess.attachments.image")}
                                    </span>
                                  </div>
                                  <div
                                    className={styles.attachmentOption}
                                    onClick={() =>
                                      handleAttachmentSelect("audio")
                                    }
                                  >
                                    <div className={styles.attachmentIcon}>
                                      <i className="fas fa-microphone" />
                                    </div>
                                    <span>
                                      {t("chatjess.attachments.audio")}
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* Emoji picker for this additional question */}
                              {activeEmojiFor === index && (
                                <div
                                  className="emoji-picker-container"
                                  style={{
                                    position: "absolute",
                                    bottom: "60px",
                                    left: "60px",
                                    zIndex: 20,
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                                  }}
                                >
                                  <Picker
                                    onEmojiClick={handleEmojiSelect}
                                    theme={
                                      isDarkTheme ? Theme.DARK : Theme.LIGHT
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`${styles.feedbackPanelTabContent} ${styles.attachmentsTab} ${activeTab === "attachments" ? styles.active : ""}`}
              >
                {(() => {
                  const allMediaItems: Array<{
                    type: "question" | "answer";
                    questionIndex: number;
                    media: MediaType;
                    question?: string;
                  }> = [];

                  feedbackMsg?.supportMessages?.forEach((msg, index) => {
                    if (msg.media) {
                      allMediaItems.push({
                        type: "question",
                        questionIndex: index,
                        media: msg.media,
                        question: msg.question,
                      });
                    }
                    if (msg.answerMedia && msg.answerMedia.length > 0) {
                      msg.answerMedia.forEach((media: MediaType) => {
                        allMediaItems.push({
                          type: "answer",
                          questionIndex: index,
                          media,
                          question: msg.question,
                        });
                      });
                    }
                  });

                  return allMediaItems.length > 0 ? (
                    <div className={styles.attachmentsGrid}>
                      {allMediaItems.map((item, idx) => (
                        <div key={idx} className={styles.attachmentCard}>
                          <div className={styles.attachmentQuestionLabel}>
                            {t(
                              `chatjess.feedback.${item.type === "question" ? "questionNumber" : "answerNumber"}`,
                              { number: item.questionIndex + 1 },
                            )}
                          </div>
                          <div
                            className={styles.attachmentFileTitle}
                            title={item.media.filename || undefined}
                          >
                            {item.media.filename ||
                              t("chatjess.attachments.attachment")}
                          </div>
                          <div className={styles.attachmentPreview}>
                            {item.media.mimetype?.startsWith("image/") ? (
                              <img
                                src={
                                  item.media.url ||
                                  `data:${item.media.mimetype};base64,${item.media.base64Data || ""}`
                                }
                                alt={
                                  item.media.filename ||
                                  t("chatjess.attachments.attachment")
                                }
                                className={styles.attachmentPreviewImage}
                                onClick={() => {
                                  if (item.media?.url) {
                                    if (isAgentProxyUrl(item.media.url)) {
                                      downloadFile(
                                        item.media.url,
                                        item.media.filename,
                                      );
                                    } else {
                                      window.open(item.media.url, "_blank");
                                    }
                                  }
                                }}
                                style={{ cursor: "pointer" }}
                              />
                            ) : (
                              <div className={styles.filePreview}>
                                <i className="fas fa-file"></i>
                                <div className={styles.attachmentFileName}>
                                  {item.media.filename ||
                                    t("chatjess.attachments.attachment")}
                                </div>
                              </div>
                            )}
                          </div>
                          <button
                            className={styles.attachmentDownloadBtn}
                            onClick={() => {
                              if (item.media?.url) {
                                if (isAgentProxyUrl(item.media.url)) {
                                  downloadFile(
                                    item.media.url,
                                    item.media.filename,
                                  );
                                } else {
                                  const link = document.createElement("a");
                                  link.href = item.media.url;
                                  link.download =
                                    item.media.filename || "attachment";
                                  link.click();
                                }
                              }
                            }}
                          >
                            <i className="fas fa-download"></i>{" "}
                            {t("chatjess.feedback.download")}
                          </button>
                          {item.question && (
                            <div className={styles.attachmentQuestion}>
                              <small>{item.question}</small>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noAttachmentsMessage}>
                      <i className="fas fa-inbox"></i>
                      <p>{t("chatjess.feedback.noAttachments")}</p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default FeedbackPanel;
